import React, { useEffect, useMemo, useState } from "react";
import { Headphones, Search, Eye, X } from "lucide-react";
import { api } from "@/lib/api";
import { Loader } from "@/components/common/Bits";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  technical_issue: "Technical Issue",
  payment_billing: "Payment / Billing",
  subscription: "Subscription",
  login_account: "Login / Account",
  pos_billing: "POS / Billing",
  menu_inventory: "Menu / Inventory",
  orders_kds: "Orders / KDS",
  other: "Other",
};

const PRIORITY_LABELS = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
};

const priorityClass = (priority) => {
  if (priority === "urgent") {
    return "bg-red-100 text-red-700";
  }

  if (priority === "high") {
    return "bg-orange-100 text-orange-700";
  }

  if (priority === "low") {
    return "bg-gray-100 text-gray-700";
  }

  return "bg-blue-100 text-blue-700";
};

const statusClass = (status) => {
  if (status === "resolved") {
    return "bg-green-100 text-green-700";
  }

  if (status === "in_progress") {
    return "bg-amber-100 text-amber-700";
  }

  return "bg-amber-100 text-amber-700";
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [resolutionNote, setResolutionNote] = useState("");

  const [page, setPage] = useState(1);

  const TICKETS_PER_PAGE = 10;

  // ---------------------------------------------------------
  // Load tickets
  // ---------------------------------------------------------
  const loadTickets = async () => {
    try {
      setLoading(true);

      const params = {};

      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      if (priorityFilter !== "all") {
        params.priority = priorityFilter;
      }

      if (search.trim()) {
        params.search = search.trim();
      }

      const response = await api.adminSupportTickets(params);

      const data = Array.isArray(response.data) ? response.data : [];

      setTickets(data);
      setPage(1);
    } catch (error) {
      console.error("Support tickets load error:", error);

      toast.error(
        error?.response?.data?.detail || "Unable to load support tickets",
      );

      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load + filter changes
  useEffect(() => {
    loadTickets();
  }, [statusFilter, priorityFilter]);

  // ---------------------------------------------------------
  // Search
  // ---------------------------------------------------------
  const handleSearch = (e) => {
    if (e.key === "Enter") {
      loadTickets();
    }
  };

  // ---------------------------------------------------------
  // Summary counts
  // ---------------------------------------------------------
  const counts = useMemo(() => {
    return {
      open: tickets.filter((t) => t.status === "open").length,

      in_progress: tickets.filter((t) => t.status === "in_progress").length,

      resolved: tickets.filter((t) => t.status === "resolved").length,
    };
  }, [tickets]);

  // ---------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------
  const totalTickets = tickets.length;

  const totalPages = Math.max(1, Math.ceil(totalTickets / TICKETS_PER_PAGE));

  const safePage = Math.min(page, totalPages);

  const paginatedTickets = useMemo(() => {
    const start = (safePage - 1) * TICKETS_PER_PAGE;

    return tickets.slice(start, start + TICKETS_PER_PAGE);
  }, [tickets, safePage]);

  const showingFrom =
    totalTickets === 0 ? 0 : (safePage - 1) * TICKETS_PER_PAGE + 1;

  const showingTo = Math.min(safePage * TICKETS_PER_PAGE, totalTickets);

  // ---------------------------------------------------------
  // Ticket detail
  // ---------------------------------------------------------
  const openTicket = async (ticketId) => {
    try {
      setDetailLoading(true);

      const response = await api.adminSupportTicket(ticketId);

      const ticket = response.data;

      setSelectedTicket(ticket);
      setNewStatus(ticket.status || "open");
      setResolutionNote(ticket.resolution_note || "");
    } catch (error) {
      console.error("Support ticket detail error:", error);

      toast.error(error?.response?.data?.detail || "Unable to load ticket");
    } finally {
      setDetailLoading(false);
    }
  };

  // ---------------------------------------------------------
  // Update ticket
  // ---------------------------------------------------------
  const updateTicket = async () => {
    if (!selectedTicket) return;

    if (newStatus === "resolved" && !resolutionNote.trim()) {
      toast.error("Resolution note is required to resolve the ticket");

      return;
    }

    try {
      setSaving(true);

      const response = await api.updateAdminSupportTicket(selectedTicket.id, {
        status: newStatus,
        resolution_note: resolutionNote.trim(),
      });

      const updated = response.data;

      setSelectedTicket(null);

      toast.success("Support ticket updated successfully");

      await loadTickets();
    } catch (error) {
      console.error("Support ticket update error:", error);

      toast.error(error?.response?.data?.detail || "Unable to update ticket");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="mx-auto w-full max-w-[1600px] space-y-6">
        {/* -------------------------------------------------
            Header
        ------------------------------------------------- */}
        <div>
          <h1 className="font-display text-3xl font-black">Support</h1>

          <p className="mt-1 text-sm text-muted-foreground">
            Manage restaurant support tickets
          </p>
        </div>

        {/* -------------------------------------------------
            Summary
        ------------------------------------------------- */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Open
            </p>

            <p className="mt-2 font-display text-3xl font-black">
              {counts.open}
            </p>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              In Progress
            </p>

            <p className="mt-2 font-display text-3xl font-black">
              {counts.in_progress}
            </p>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Resolved
            </p>

            <p className="mt-2 font-display text-3xl font-black">
              {counts.resolved}
            </p>
          </div>
        </div>

        {/* -------------------------------------------------
            Filters
        ------------------------------------------------- */}
        <div className="rounded-2xl border border-border bg-card p-4 sm:p-5">
          <div className="flex flex-col gap-3 xl:flex-row">
            <div className="relative min-w-0 flex-1">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearch}
                placeholder="Search ticket, restaurant, owner or subject..."
                className="h-11 w-full rounded-xl border border-border bg-white pl-10 pr-3 text-sm outline-none focus:border-primary"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-11 rounded-xl border border-border bg-white px-3 text-sm xl:w-44"
            >
              <option value="all">All Status</option>

              <option value="open">Open</option>

              <option value="in_progress">In Progress</option>

              <option value="resolved">Resolved</option>
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="h-11 rounded-xl border border-border bg-white px-3 text-sm xl:w-44"
            >
              <option value="all">All Priority</option>

              <option value="urgent">Urgent</option>

              <option value="high">High</option>

              <option value="medium">Medium</option>

              <option value="low">Low</option>
            </select>

            <button
              type="button"
              onClick={loadTickets}
              className="h-11 rounded-xl bg-primary px-5 text-sm font-semibold text-white transition hover:opacity-90"
            >
              Search
            </button>
          </div>
        </div>

        {/* -------------------------------------------------
            Tickets
        ------------------------------------------------- */}
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          {loading ? (
            <div className="p-10">
              <Loader label="Loading support tickets..." />
            </div>
          ) : totalTickets === 0 ? (
            <div className="px-6 py-16 text-center">
              <Headphones size={40} className="mx-auto text-muted-foreground" />

              <p className="mt-4 font-semibold">No support tickets found</p>

              <p className="mt-1 text-sm text-muted-foreground">
                Restaurant support requests will appear here.
              </p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[1100px] text-sm">
                  <thead className="bg-secondary/50">
                    <tr className="text-left">
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                        Ticket
                      </th>

                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                        Restaurant
                      </th>

                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                        Subject
                      </th>

                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                        Category
                      </th>

                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                        Priority
                      </th>

                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                        Status
                      </th>

                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                        Created
                      </th>

                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedTickets.map((ticket) => (
                      <tr
                        key={ticket.id}
                        className="border-t border-border transition-colors hover:bg-secondary/20"
                      >
                        <td className="px-5 py-4 font-semibold">
                          {ticket.ticket_id}
                        </td>

                        <td className="px-5 py-4">
                          <div className="font-semibold">
                            {ticket.restaurant_name}
                          </div>

                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {ticket.owner_email}
                          </div>
                        </td>

                        <td className="max-w-xs px-5 py-4">
                          <div
                            className="truncate font-medium"
                            title={ticket.subject}
                          >
                            {ticket.subject}
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          {CATEGORY_LABELS[ticket.category] || "Other"}
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${priorityClass(
                              ticket.priority,
                            )}`}
                          >
                            {PRIORITY_LABELS[ticket.priority] || "Medium"}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(
                              ticket.status,
                            )}`}
                          >
                            {STATUS_LABELS[ticket.status] || "Open"}
                          </span>
                        </td>

                        <td className="whitespace-nowrap px-5 py-4 text-xs text-muted-foreground">
                          {formatDate(ticket.created_at)}
                        </td>

                        <td className="px-5 py-4">
                          <button
                            type="button"
                            onClick={() => openTicket(ticket.id)}
                            className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-xs font-semibold transition hover:bg-secondary"
                          >
                            <Eye size={15} />
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col gap-4 border-t border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing{" "}
                  <span className="font-semibold text-foreground">
                    {showingFrom}
                  </span>{" "}
                  to{" "}
                  <span className="font-semibold text-foreground">
                    {showingTo}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-foreground">
                    {totalTickets}
                  </span>{" "}
                  tickets
                </p>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="h-9 rounded-lg border border-border bg-white px-3 text-xs font-semibold transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Previous
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from(
                      {
                        length: totalPages,
                      },
                      (_, i) => i + 1,
                    ).map((pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        onClick={() => setPage(pageNumber)}
                        className={
                          safePage === pageNumber
                            ? "grid h-9 min-w-9 place-items-center rounded-lg bg-primary px-2 text-xs font-semibold text-white"
                            : "grid h-9 min-w-9 place-items-center rounded-lg border border-border bg-white px-2 text-xs font-semibold text-foreground transition hover:bg-secondary"
                        }
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="h-9 rounded-lg border border-border bg-white px-3 text-xs font-semibold transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ---------------------------------------------------
          Ticket Detail Modal
      --------------------------------------------------- */}
      {selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-white px-6 py-4">
              <div>
                <h2 className="font-display text-xl font-bold">
                  {selectedTicket.ticket_id}
                </h2>

                <p className="text-xs text-muted-foreground">
                  {formatDate(selectedTicket.created_at)}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedTicket(null)}
                className="rounded-lg p-2 transition hover:bg-secondary"
              >
                <X size={18} />
              </button>
            </div>

            {detailLoading ? (
              <div className="p-10">
                <Loader label="Loading ticket..." />
              </div>
            ) : (
              <div className="space-y-5 p-6">
                {/* Basic info */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Restaurant</p>

                    <p className="font-semibold">
                      {selectedTicket.restaurant_name}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">Owner</p>

                    <p className="font-semibold">{selectedTicket.owner_name}</p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>

                    <p className="break-all font-semibold">
                      {selectedTicket.owner_email}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">Phone</p>

                    <p className="font-semibold">
                      {selectedTicket.owner_phone || "—"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">Category</p>

                    <p className="font-semibold">
                      {CATEGORY_LABELS[selectedTicket.category] || "Other"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">Priority</p>

                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${priorityClass(
                        selectedTicket.priority,
                      )}`}
                    >
                      {PRIORITY_LABELS[selectedTicket.priority] || "Medium"}
                    </span>
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <p className="text-xs text-muted-foreground">Subject</p>

                  <p className="mt-1 font-semibold">{selectedTicket.subject}</p>
                </div>

                {/* Description */}
                <div>
                  <p className="text-xs text-muted-foreground">Description</p>

                  <div className="mt-1 whitespace-pre-wrap rounded-xl border border-border bg-secondary/20 p-4 text-sm">
                    {selectedTicket.description}
                  </div>
                </div>

                {/* Status */}
                <div>
                  <p className="mb-2 text-xs text-muted-foreground">Status</p>

                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    disabled={selectedTicket.status === "resolved"}
                    className="h-11 w-full rounded-xl border border-border bg-white px-3 text-sm"
                  >
                    <option value="open">Open</option>

                    <option value="in_progress">In Progress</option>

                    <option value="resolved">Resolved</option>
                  </select>
                </div>

                {/* Resolution */}
                <div>
                  <p className="mb-2 text-xs text-muted-foreground">
                    Resolution Note
                  </p>

                  <textarea
                    value={resolutionNote}
                    onChange={(e) => setResolutionNote(e.target.value)}
                    disabled={selectedTicket.status === "resolved"}
                    placeholder="Write what was fixed / resolved..."
                    rows={4}
                    className="w-full rounded-xl border border-border bg-white p-3 text-sm outline-none focus:border-primary disabled:bg-secondary/50"
                  />
                </div>

                {/* Resolved details */}
                {selectedTicket.resolved_at && (
                  <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                    <p className="text-xs font-semibold text-green-700">
                      Resolved
                    </p>

                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatDate(selectedTicket.resolved_at)}
                    </p>

                    {selectedTicket.resolution_note && (
                      <p className="mt-2 whitespace-pre-wrap text-sm">
                        {selectedTicket.resolution_note}
                      </p>
                    )}
                  </div>
                )}

                {/* Action */}
                <button
                  type="button"
                  onClick={updateTicket}
                  disabled={saving || selectedTicket.status === "resolved"}
                  className="h-11 w-full rounded-xl bg-primary font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving
                    ? "Saving..."
                    : selectedTicket.status === "resolved"
                      ? "Ticket Resolved"
                      : "Update Ticket"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
